import type { IPascoRepository } from "@src/domain/pascos/repository/pasco_repository";
import type { PascoDTO } from "../dto";
import { PascoDtoMapper } from "../mappers/pasco-dto.mapper";
import type { StudentUploadPascoData } from "@src/domain/pascos/value_objects/student_upload_pasco_data";

/**
 * Use Case: Student Upload PASCO
 * Allows students to upload PASCO files with optional university selection
 */
export class StudentUploadPascoUseCase {
    constructor(private readonly pascoRepository: IPascoRepository) { }

    /**
     * Execute the use case
     * @param data - The student upload data (file and optional university_id)
     * @param onUploadProgress - Optional callback for upload progress
     * @returns Promise<PascoDTO> - The uploaded PASCO DTO
     * @throws Error if validation fails
     */
    async execute(
        data: StudentUploadPascoData,
        onUploadProgress?: (progress: number) => void
    ): Promise<PascoDTO> {
        this.validateUploadData(data);
        const pasco = await this.pascoRepository.studentUpload(data, onUploadProgress);
        return PascoDtoMapper.toDTO(pasco);
    }

    /**
     * Validate upload data
     * @param data - The data to validate
     * @throws Error if validation fails
     */
    private validateUploadData(data: StudentUploadPascoData): void {
        if (!data.file) {
            throw new Error('File is required');
        }

        // Validate file type
        if (data.file.type !== 'application/pdf') {
            throw new Error('Only PDF files are allowed');
        }

        // Validate file size (50MB max)
        const maxSize = 50 * 1024 * 1024;
        if (data.file.size > maxSize) {
            throw new Error('File size must be less than 50MB');
        }

        // University ID is optional, no validation needed
    }
}
